import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import StaffNotation from '@/components/feature/StaffNotation';
import PianoKeyboard from '@/components/feature/PianoKeyboard';
import Button from '@/components/base/Button';
import Badge from '@/components/base/Badge';
import { useMetronome } from '@/hooks/useMetronome';
import { getAudioContext, playMidi, playSequence } from '@/lib/audio/synth';
import { getNote } from '@/lib/music/notes';
import type { MetronomeBeat } from '@/lib/audio/metronome';

/** Uma semínima por pulso, quatro pulsos por compasso (4/4). */
const MELODY = ['C4', 'D4', 'E4', 'F4'];
const BEATS_PER_BAR = 4;

interface RhythmSlot {
  global: number;
  noteId: string;
  time: number;
  graded: boolean;
}

interface FeedbackState {
  tone: 'ok' | 'warn' | 'error';
  text: string;
}

interface RhythmStats {
  total: number;
  onTime: number;
  offTime: number;
  wrong: number;
}

const EMPTY_STATS: RhythmStats = { total: 0, onTime: 0, offTime: 0, wrong: 0 };

export default function RhythmPage() {
  const [bpmInput, setBpmInput] = useState(80);
  const [slowMode, setSlowMode] = useState(false);
  const [phase, setPhase] = useState<'idle' | 'running'>('idle');
  const [feedback, setFeedback] = useState<FeedbackState | null>(null);
  const [stats, setStats] = useState<RhythmStats>(EMPTY_STATS);

  const slotsRef = useRef<RhythmSlot[]>([]);
  const globalRef = useRef(0);

  const effectiveBpm = slowMode ? Math.max(30, Math.round(bpmInput / 2)) : bpmInput;

  const handleBeat = useCallback((beat: MetronomeBeat) => {
    const global = globalRef.current;
    globalRef.current += 1;
    slotsRef.current.push({
      global,
      noteId: MELODY[global % MELODY.length],
      time: beat.time,
      graded: false,
    });
    const ctx = getAudioContext();
    const now = ctx ? ctx.currentTime : 0;
    slotsRef.current = slotsRef.current.filter((slot) => slot.time > now - 3);
  }, []);

  const metronome = useMetronome({
    initialBpm: bpmInput,
    beatsPerBar: BEATS_PER_BAR,
    onBeat: handleBeat,
  });

  const setMetronomeBpm = metronome.setBpm;
  const stopMetronome = metronome.stop;

  useEffect(() => {
    setMetronomeBpm(effectiveBpm);
  }, [effectiveBpm, setMetronomeBpm]);

  const currentBeat = metronome.currentBeat;
  const nextBeatIndex = currentBeat < 0 ? 0 : (currentBeat + 1) % BEATS_PER_BAR;
  const expectedNoteId = MELODY[nextBeatIndex];
  const tolerance = slowMode ? 0.3 : 0.17;

  const start = () => {
    slotsRef.current = [];
    globalRef.current = 0;
    setStats(EMPTY_STATS);
    setFeedback(null);
    metronome.setBpm(effectiveBpm);
    metronome.start();
    setPhase('running');
  };

  const stop = () => {
    metronome.stop();
    setPhase('idle');
  };

  const playExample = () => {
    const midis = MELODY.map((id) => getNote(id).midi);
    playSequence(midis, 60 / effectiveBpm, (60 / effectiveBpm) * 0.9);
  };

  const gradeKey = useCallback(
    (midi: number) => {
      playMidi(midi, 0.55);
      if (phase !== 'running') return;

      const ctx = getAudioContext();
      const now = ctx ? ctx.currentTime : 0;
      let best: RhythmSlot | null = null;
      let bestAbs = Infinity;
      slotsRef.current.forEach((slot) => {
        if (slot.graded) return;
        const delta = now - slot.time;
        if (delta < -0.45 || delta > 0.6) return;
        const abs = Math.abs(delta);
        if (abs < bestAbs) {
          bestAbs = abs;
          best = slot;
        }
      });

      if (!best) {
        setFeedback({ tone: 'warn', text: 'Fora do tempo. Espere o próximo pulso.' });
        return;
      }

      const slot = best as RhythmSlot;
      slot.graded = true;
      const targetMidi = getNote(slot.noteId).midi;
      const delta = now - slot.time;
      const pitchOk = midi === targetMidi;

      if (!pitchOk) {
        setFeedback({ tone: 'error', text: `Nota errada. Era ${getNote(slot.noteId).label}.` });
        setStats((s) => ({ ...s, total: s.total + 1, wrong: s.wrong + 1 }));
        return;
      }

      if (Math.abs(delta) <= tolerance) {
        setFeedback({ tone: 'ok', text: 'Certo! Nota correta no pulso.' });
        setStats((s) => ({ ...s, total: s.total + 1, onTime: s.onTime + 1 }));
      } else if (delta < 0) {
        setFeedback({ tone: 'warn', text: 'Nota certa, mas um pouco cedo.' });
        setStats((s) => ({ ...s, total: s.total + 1, offTime: s.offTime + 1 }));
      } else {
        setFeedback({ tone: 'warn', text: 'Nota certa, mas um pouco atrasada.' });
        setStats((s) => ({ ...s, total: s.total + 1, offTime: s.offTime + 1 }));
      }
    },
    [phase, tolerance],
  );

  useEffect(() => () => stopMetronome(), [stopMetronome]);

  const feedbackClasses = useMemo(() => {
    if (!feedback) return '';
    if (feedback.tone === 'ok') return 'bg-accent-100 text-accent-900';
    if (feedback.tone === 'error') return 'bg-secondary-100 text-secondary-900';
    return 'bg-primary-100 text-primary-900';
  }, [feedback]);

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <section className="w-full bg-background-100 px-4 md:px-8 py-12 md:py-16">
          <div className="mx-auto max-w-6xl">
            <Badge tone="accent" icon="ri-timer-line">
              Aula demonstrativa de ritmo
            </Badge>
            <h1 className="mt-4 font-heading text-3xl font-extrabold text-foreground-950 md:text-4xl">
              Toque junto com o metrônomo no compasso 4/4
            </h1>
            <p className="mt-4 max-w-3xl text-foreground-700">
              No compasso 4/4 contamos “1, 2, 3, 4” e cada número é um pulso de semínima. Toque uma
              nota por pulso, na tecla que aparece na pauta. Repare que quatro pulsos por compasso
              não significam quatro notas — mais adiante, duas colcheias vão ocupar juntas um único
              pulso.
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
            <div className="flex flex-col gap-6">
              {/* Controles */}
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <div className="flex flex-wrap items-center gap-4">
                  <Button
                    variant={phase === 'running' ? 'secondary' : 'primary'}
                    size="lg"
                    icon={phase === 'running' ? 'ri-stop-circle-line' : 'ri-play-circle-line'}
                    onClick={phase === 'running' ? stop : start}
                  >
                    {phase === 'running' ? 'Parar' : 'Começar'}
                  </Button>
                  <Button variant="outline" icon="ri-volume-up-line" onClick={playExample}>
                    Ouvir exemplo
                  </Button>
                  <label className="flex items-center gap-2 text-sm font-label font-semibold text-foreground-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={slowMode}
                      onChange={(event) => setSlowMode(event.target.checked)}
                      className="h-4 w-4 accent-primary-500"
                    />
                    Velocidade reduzida
                  </label>
                </div>

                <div className="mt-5">
                  <div className="mb-2 flex items-center justify-between">
                    <label htmlFor="bpm" className="font-label text-sm font-semibold text-foreground-700">
                      Andamento
                    </label>
                    <span className="font-heading text-lg font-bold text-primary-700">
                      {effectiveBpm} BPM
                      {slowMode ? (
                        <span className="ml-2 text-xs font-label font-semibold text-foreground-500">
                          (reduzido)
                        </span>
                      ) : null}
                    </span>
                  </div>
                  <input
                    id="bpm"
                    type="range"
                    min={40}
                    max={160}
                    step={5}
                    value={bpmInput}
                    onChange={(event) => setBpmInput(Number(event.target.value))}
                    className="h-2 w-full cursor-pointer appearance-none rounded-full bg-background-200 accent-primary-500"
                  />
                  <div className="mt-1 flex justify-between text-xs text-foreground-400">
                    <span>40</span>
                    <span>100</span>
                    <span>160</span>
                  </div>
                </div>
              </div>

              {/* Pulsos */}
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <div className="mb-4 flex items-center justify-between">
                  <span className="font-label text-sm font-bold text-foreground-800">
                    Contagem do compasso
                  </span>
                  <Badge tone={phase === 'running' ? 'accent' : 'neutral'}>
                    {phase === 'running' ? 'Tocando' : 'Parado'}
                  </Badge>
                </div>
                <div className="flex items-center justify-center gap-3 md:gap-5">
                  {[0, 1, 2, 3].map((beat) => {
                    const active = phase === 'running' && currentBeat === beat;
                    return (
                      <div
                        key={beat}
                        className={`w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full border font-heading text-xl font-bold transition-all duration-100 ${
                          active
                            ? 'scale-110 border-primary-500 bg-primary-500 text-background-50'
                            : 'border-background-300 bg-background-100 text-foreground-500'
                        }`}
                      >
                        {beat + 1}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Nota esperada */}
              <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
                <div className="flex items-center justify-between">
                  <Badge tone="primary" icon="ri-eye-line">
                    {phase === 'running' ? 'Toque agora' : 'Prévia da melodia'}
                  </Badge>
                  <span className="text-xs font-label font-semibold text-foreground-500">
                    Pulso {nextBeatIndex + 1} de 4
                  </span>
                </div>
                <div className="mt-4 rounded-lg bg-background-100 p-3 md:p-4">
                  <StaffNotation
                    notes={[expectedNoteId]}
                    timeSignature="4/4"
                    highlightIndex={0}
                    showNoteNames
                    space={26}
                  />
                </div>

                <div className="mt-4 min-h-[52px]">
                  {feedback ? (
                    <div className={`flex items-start gap-3 rounded-lg px-4 py-3 text-sm animate-pop ${feedbackClasses}`}>
                      <i className="ri-information-line w-5 h-5 mt-0.5 flex items-center justify-center" />
                      <span>{feedback.text}</span>
                    </div>
                  ) : (
                    <p className="text-sm text-foreground-500">
                      {phase === 'running'
                        ? 'Siga o pulso e toque a nota mostrada na tecla correspondente.'
                        : 'Clique em “Começar” e toque uma nota por pulso.'}
                    </p>
                  )}
                </div>

                <div className="mt-4 rounded-lg border border-background-200 bg-background-100 p-3">
                  <PianoKeyboard
                    highlightMidis={[getNote(expectedNoteId).midi]}
                    showNoteNames
                    silent
                    onKeyDown={gradeKey}
                  />
                  <p className="mt-2 text-center text-xs text-foreground-500 md:hidden">
                    Deslize o teclado para os lados para ver todas as teclas.
                  </p>
                </div>
              </div>
            </div>

            {/* Lateral */}
            <aside className="flex flex-col gap-6">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">
                  Seu desempenho
                </h2>
                <div className="mt-4 flex flex-col gap-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-foreground-700">
                      <i className="ri-check-double-line w-4 h-4 flex items-center justify-center text-accent-600" />
                      No pulso
                    </span>
                    <strong className="font-heading text-foreground-950">{stats.onTime}</strong>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-foreground-700">
                      <i className="ri-timer-line w-4 h-4 flex items-center justify-center text-primary-600" />
                      Fora do tempo
                    </span>
                    <strong className="font-heading text-foreground-950">{stats.offTime}</strong>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-foreground-700">
                      <i className="ri-close-line w-4 h-4 flex items-center justify-center text-secondary-600" />
                      Nota errada
                    </span>
                    <strong className="font-heading text-foreground-950">{stats.wrong}</strong>
                  </div>
                  <div className="flex items-center justify-between border-t border-background-200 pt-3 text-sm">
                    <span className="text-foreground-700">Toques avaliados</span>
                    <strong className="font-heading text-foreground-950">{stats.total}</strong>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-background-200 bg-accent-50 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">
                  Sobre o compasso 4/4
                </h2>
                <ul className="mt-3 flex flex-col gap-2.5 text-sm text-foreground-800">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill w-5 h-5 mt-0.5 flex items-center justify-center text-accent-500" />
                    Quatro pulsos por compasso.
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill w-5 h-5 mt-0.5 flex items-center justify-center text-accent-500" />
                    Cada pulso vale uma semínima.
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill w-5 h-5 mt-0.5 flex items-center justify-center text-accent-500" />
                    O primeiro pulso do compasso é o mais acentuado.
                  </li>
                </ul>
                <Link
                  to="/aulas/10"
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-label font-semibold text-primary-700 hover:text-primary-800 cursor-pointer"
                >
                  <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center" />
                  Revisar a leitura das notas
                </Link>
              </div>
            </aside>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}